import { FunctionComponent, useCallback } from 'react';
import { Button, Popconfirm, Tag, Tooltip } from 'antd';
import { LoadingOutlined, LockOutlined } from '@ant-design/icons';
import UserBlockMenu from '@components/UserBlockMenu';
import IUser from '@interfaces/IUser';
import css from './BlockingUserButton.module.less';
import type { UserBlockReason } from '@constants/userBlockOptions';

interface BlockUserTooltipProps {
  user: IUser;
  value?: boolean;
  onChange?: (value: boolean) => void;
  content: any;
  unblock: (user: IUser) => void;
  block: (
    user: IUser,
    daysToUnblock?: number,
    daysToClean?: number,
    reason?: UserBlockReason,
  ) => void;
  processing: boolean;
}

const BlockUserTooltip: FunctionComponent<BlockUserTooltipProps> = ({
  value,
  onChange,
  user,
  content,
  unblock,
  block,
  processing,
}) => {
  const isBlocked = !(value ?? user.enable);

  const onUnblock = useCallback(() => {
    unblock?.(user);
    onChange?.(true);
  }, [onChange, unblock, user]);

  const onBlock = useCallback(
    (user: IUser, daysToUnblock?: number, daysToClean?: number, reason?: UserBlockReason) => {
      block?.(user, daysToUnblock, daysToClean, reason);
      onChange?.(false);
    },
    [block, onChange],
  );

  return (
    <div className={css.mainContainer}>
      {isBlocked ? (
        <Popconfirm
          title={content.unblockConfirmText}
          onConfirm={onUnblock}
        >
          <Tag color='error'>
            {processing ? (
              <LoadingOutlined />
            ) : (
              <span className={css.blocked}>{content.blocked}</span>
            )}
          </Tag>
        </Popconfirm>
      ) : (
        <Tooltip title={isBlocked ? content.unblock : content.block}>
          {!isBlocked && (
            <UserBlockMenu
              user={user}
              onBlock={onBlock}
            >
              <Button
                disabled={processing}
                loading={processing}
                icon={<LockOutlined />}
                shape='circle'
              />
            </UserBlockMenu>
          )}
        </Tooltip>
      )}
    </div>
  );
};

export default BlockUserTooltip;
